/**
 * TaksasiPagination Component
 * 
 * Pagination controls for Taksasi table
 */

import React from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  HStack,
  Select,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { PaginationMeta } from '../types';

interface TaksasiPaginationProps {
  pagination: PaginationMeta;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

const TaksasiPagination: React.FC<TaksasiPaginationProps> = ({
  pagination,
  onPageChange,
  onLimitChange,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { page, limit, total_items, total_pages } = pagination;

  const startItem = total_items === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total_items);

  const canGoPrevious = page > 1;
  const canGoNext = page < total_pages;

  const handleFirstPage = () => onPageChange(1);
  const handlePreviousPage = () => onPageChange(Math.max(1, page - 1));
  const handleNextPage = () => onPageChange(Math.min(total_pages, page + 1));
  const handleLastPage = () => onPageChange(total_pages);

  if (total_items === 0) {
    return null;
  }

  return (
    <Box
      bg="white"
      p={4}
      borderRadius="xl"
      boxShadow="sm"
      border="1px"
      borderColor="gray.200"
    >
      <Flex
        justify="space-between"
        align="center"
        direction={{ base: 'column', md: 'row' }}
        gap={4}
      >
        {/* Info Text */}
        <Text fontSize="sm" color="gray.600" textAlign={{ base: 'center', md: 'left' }}>
          Menampilkan <strong>{startItem}</strong> - <strong>{endItem}</strong> dari{' '}
          <strong>{total_items}</strong> data
        </Text>

        {/* Pagination Controls */}
        <HStack spacing={2}>
          {/* First Page Button */}
          {!isMobile && (
            <IconButton
              aria-label="First page"
              icon={<FiChevronsLeft />}
              onClick={handleFirstPage}
              isDisabled={!canGoPrevious}
              size="sm"
              variant="outline"
              borderRadius="lg"
            />
          )}

          {/* Previous Button */}
          <IconButton
            aria-label="Previous page"
            icon={<FiChevronLeft />}
            onClick={handlePreviousPage}
            isDisabled={!canGoPrevious}
            size="sm"
            variant="outline"
            borderRadius="lg"
          />

          {/* Page Info */}
          <Box
            px={4}
            py={1.5}
            bg="gray.50"
            borderRadius="lg"
            border="1px"
            borderColor="gray.200"
          >
            <Text fontSize="sm" fontWeight="medium" color="gray.700">
              Hal. {page} / {total_pages}
            </Text>
          </Box>

          {/* Next Button */}
          <IconButton
            aria-label="Next page"
            icon={<FiChevronRight />}
            onClick={handleNextPage}
            isDisabled={!canGoNext}
            size="sm"
            variant="outline"
            borderRadius="lg"
          />

          {/* Last Page Button */}
          {!isMobile && (
            <IconButton
              aria-label="Last page"
              icon={<FiChevronsRight />}
              onClick={handleLastPage}
              isDisabled={!canGoNext}
              size="sm"
              variant="outline"
              borderRadius="lg"
            />
          )}
        </HStack>

        {/* Items per page selector */}
        {onLimitChange && !isMobile && (
          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.600">
              Per halaman:
            </Text>
            <Select
              size="sm"
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              width="80px"
              borderRadius="lg"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Select>
          </HStack>
        )}
      </Flex>
    </Box>
  );
};

export default TaksasiPagination;
